'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

import {
  type TenantAnalyticsConsentSnapshot,
  createDeniedTenantAnalyticsConsent,
} from '@kit/analytics/tenant';

// Version 3: browser-derived pixel page metadata is now suppressed unless the
// document location is privacy-safe. Keeping the policy version in both the
// storage key and payload makes every semantic revision fail closed and show
// the prompt again instead of inheriting an older grant.
export const TENANT_ANALYTICS_CONSENT_POLICY_VERSION = '3';

interface TenantAnalyticsConsentContextValue {
  readonly consent: TenantAnalyticsConsentSnapshot;
  readonly hasDecision: boolean;
  grantAll(): void;
  rejectNonEssential(): void;
  updateConsent(
    consent: Pick<TenantAnalyticsConsentSnapshot, 'analytics' | 'advertising'>,
  ): void;
  resetConsent(): void;
}

const TenantAnalyticsConsentContext =
  createContext<TenantAnalyticsConsentContextValue | null>(null);

const CONSENT_CHANGE_EVENT = 'tenant-analytics-consent-change';
const STORAGE_PREFIX = 'tenant-analytics-consent';
const UNKNOWN_UPDATED_AT = '1970-01-01T00:00:00.000Z';

export function TenantAnalyticsConsentBoundary({
  children,
  tenantId,
}: React.PropsWithChildren<{ tenantId: string | null | undefined }>) {
  if (!tenantId) {
    return children;
  }

  return (
    <TenantAnalyticsConsentProvider tenantId={tenantId}>
      {children}
    </TenantAnalyticsConsentProvider>
  );
}

export function TenantAnalyticsConsentProvider({
  children,
  tenantId,
  policyVersion = TENANT_ANALYTICS_CONSENT_POLICY_VERSION,
}: React.PropsWithChildren<{
  tenantId: string;
  policyVersion?: string;
}>) {
  const storageKey = getConsentStorageKey(tenantId, policyVersion);
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      subscribeToConsentChanges(storageKey, onStoreChange),
    [storageKey],
  );
  const getSnapshot = useCallback(
    () => readStoredConsent(storageKey),
    [storageKey],
  );
  const serializedConsent = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  const storedConsent = useMemo(
    () => parseStoredConsent(serializedConsent, policyVersion),
    [policyVersion, serializedConsent],
  );
  const consent = useMemo(
    () =>
      storedConsent ??
      createDeniedTenantAnalyticsConsent(policyVersion, UNKNOWN_UPDATED_AT),
    [policyVersion, storedConsent],
  );

  const updateConsent = useCallback(
    (
      selection: Pick<
        TenantAnalyticsConsentSnapshot,
        'analytics' | 'advertising'
      >,
    ) => {
      const nextConsent: TenantAnalyticsConsentSnapshot = {
        necessary: true,
        analytics: selection.analytics,
        advertising: selection.advertising,
        policyVersion,
        updatedAt: new Date().toISOString(),
      };

      storeConsent(storageKey, nextConsent);
    },
    [policyVersion, storageKey],
  );

  const grantAll = useCallback(() => {
    updateConsent({ analytics: true, advertising: true });
  }, [updateConsent]);

  const rejectNonEssential = useCallback(() => {
    updateConsent({ analytics: false, advertising: false });
  }, [updateConsent]);

  const resetConsent = useCallback(() => {
    removeStoredConsent(storageKey);
  }, [storageKey]);

  const value = useMemo<TenantAnalyticsConsentContextValue>(
    () => ({
      consent,
      hasDecision: storedConsent !== null,
      grantAll,
      rejectNonEssential,
      updateConsent,
      resetConsent,
    }),
    [
      consent,
      grantAll,
      rejectNonEssential,
      resetConsent,
      storedConsent,
      updateConsent,
    ],
  );

  return (
    <TenantAnalyticsConsentContext.Provider value={value}>
      {children}
    </TenantAnalyticsConsentContext.Provider>
  );
}

export function useTenantAnalyticsConsent() {
  const context = useContext(TenantAnalyticsConsentContext);

  if (!context) {
    throw new Error(
      'useTenantAnalyticsConsent must be used within TenantAnalyticsConsentProvider',
    );
  }

  return context;
}

export function getConsentStorageKey(tenantId: string, policyVersion: string) {
  return `${STORAGE_PREFIX}:${encodeURIComponent(tenantId)}:${encodeURIComponent(policyVersion)}`;
}

export function parseStoredConsent(
  value: string | null,
  policyVersion: string,
): TenantAnalyticsConsentSnapshot | null {
  if (!value) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);

    if (
      !isRecord(parsed) ||
      parsed.necessary !== true ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.advertising !== 'boolean' ||
      parsed.policyVersion !== policyVersion ||
      typeof parsed.updatedAt !== 'string' ||
      !Number.isFinite(Date.parse(parsed.updatedAt))
    ) {
      return null;
    }

    return {
      necessary: true,
      analytics: parsed.analytics,
      advertising: parsed.advertising,
      policyVersion,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

function subscribeToConsentChanges(
  storageKey: string,
  onStoreChange: () => void,
) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      onStoreChange();
    }
  };
  const onLocalChange = (event: Event) => {
    if (event instanceof CustomEvent && event.detail === storageKey) {
      onStoreChange();
    }
  };

  window.addEventListener('storage', onStorage);
  window.addEventListener(CONSENT_CHANGE_EVENT, onLocalChange);

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CONSENT_CHANGE_EVENT, onLocalChange);
  };
}

function readStoredConsent(storageKey: string) {
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function getServerSnapshot() {
  return null;
}

function storeConsent(
  storageKey: string,
  consent: TenantAnalyticsConsentSnapshot,
) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(consent));
    notifyConsentChange(storageKey);
  } catch {
    // Storage can be unavailable in privacy modes. Consent remains denied.
  }
}

function removeStoredConsent(storageKey: string) {
  try {
    window.localStorage.removeItem(storageKey);
    notifyConsentChange(storageKey);
  } catch {
    // Storage can be unavailable in privacy modes. Consent remains denied.
  }
}

function notifyConsentChange(storageKey: string) {
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, { detail: storageKey }),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
